/**
 * StatusBadge Component
 * 
 * Unified status indicator badge using MUI components.
 * Supports multiple variants: chip, toggle, circular, dot.
 * 
 * @example
 * // Simple status badge
 * <StatusBadge label="Active" color="#22c55e" />
 * 
 * // Toggle variant
 * <StatusBadge 
 *   variant="toggle" 
 *   isActive={isActive} 
 *   onToggle={() => setIsActive(!isActive)} 
 * />
 * 
 * // Circular badge (for detail pages)
 * <StatusBadge variant="circular" label="Completed" color="#10b981" size="large" />
 * 
 * // With status object
 * <StatusBadge status={{ statusName: 'In Progress', statusColor: '#0ea5e9' }} />
 */

import React from 'react';
import {
  Chip,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Circle as CircleIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';

// =============================================================================
// TYPES
// =============================================================================

export type StatusBadgeVariant = 'chip' | 'toggle' | 'circular' | 'dot' | 'pill';
export type StatusBadgeSize = 'small' | 'medium' | 'large' | 'sm' | 'md' | 'lg';

export interface StatusObject {
  statusName: string;
  statusColor?: string;
}

export interface StatusBadgeProps {
  /** Status label text */
  label?: string;
  /** Status color (hex or CSS color) */
  color?: string;
  /** Status object with name and color */
  status?: StatusObject;
  /** Display variant */
  variant?: StatusBadgeVariant;
  /** Size of the badge */
  size?: StatusBadgeSize;
  /** Active state (for toggle variant) */
  isActive?: boolean;
  /** Toggle handler (for toggle variant) */
  onToggle?: () => void;
  /** Loading state */
  isLoading?: boolean;
  /** Active text (for toggle variant) */
  activeText?: string;
  /** Inactive text (for toggle variant) */
  inactiveText?: string;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Additional className for Tailwind styling */
  className?: string;
  /** Custom styles */
  sx?: object;
}

// =============================================================================
// HELPERS
// =============================================================================

const getContrastColor = (hexColor: string): string => {
  // Remove # if present
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

const getLightColor = (hexColor: string, opacity = 0.15): string => {
  return `${hexColor}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
};

const sizeMap: Record<StatusBadgeSize, { chip: 'small' | 'medium'; circular: number; fontSize: string }> = {
  small: { chip: 'small', circular: 64, fontSize: '0.7rem' },
  sm: { chip: 'small', circular: 64, fontSize: '0.7rem' }, // alias
  medium: { chip: 'medium', circular: 80, fontSize: '0.75rem' },
  md: { chip: 'medium', circular: 80, fontSize: '0.75rem' }, // alias
  large: { chip: 'medium', circular: 96, fontSize: '0.875rem' },
  lg: { chip: 'medium', circular: 96, fontSize: '0.875rem' }, // alias
};

// =============================================================================
// COMPONENT
// =============================================================================

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  label,
  color,
  status,
  variant = 'chip',
  size = 'small',
  isActive,
  onToggle,
  isLoading = false,
  activeText = 'Active',
  inactiveText = 'Inactive',
  icon,
  className = '',
  sx = {},
}) => {
  // Resolve label and color from status object if provided
  const resolvedLabel = label || status?.statusName || '';
  const resolvedColor = color || status?.statusColor || '#6b7280';
  const textColor = getContrastColor(resolvedColor);
  const lightBgColor = getLightColor(resolvedColor);

  // Handle toggle click
  const handleToggle = () => {
    if (onToggle && !isLoading) {
      onToggle();
    }
  };

  // =============================================================================
  // VARIANT: Toggle
  // =============================================================================
  if (variant === 'toggle') {
    const isOn = isActive ?? false;
    const displayLabel = isOn ? activeText : inactiveText;
    const toggleColor = isOn ? '#22c55e' : '#6b7280';

    return (
      <Chip
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isLoading ? (
              <CircularProgress size={14} color="inherit" />
            ) : isOn ? (
              <ToggleOnIcon sx={{ fontSize: 18 }} />
            ) : (
              <ToggleOffIcon sx={{ fontSize: 18 }} />
            )}
            <span>{displayLabel}</span>
          </Box>
        }
        size={sizeMap[size].chip}
        onClick={onToggle ? handleToggle : undefined}
        sx={{
          backgroundColor: getLightColor(toggleColor),
          color: toggleColor,
          fontWeight: 500,
          cursor: onToggle ? 'pointer' : 'default',
          '&:hover': onToggle ? { opacity: 0.8 } : {},
          ...sx,
        }}
        className={className}
      />
    );
  }

  // =============================================================================
  // VARIANT: Circular (for detail pages)
  // =============================================================================
  if (variant === 'circular') {
    const circleSize = sizeMap[size].circular;

    return (
      <Box
        className={className}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: circleSize,
          height: circleSize,
          borderRadius: '50%',
          backgroundColor: lightBgColor,
          padding: 1,
          ...sx,
        }}
      >
        {icon || <CheckCircleIcon sx={{ color: resolvedColor, fontSize: circleSize * 0.25 }} />}
        <Typography
          sx={{
            color: resolvedColor,
            fontSize: sizeMap[size].fontSize,
            fontWeight: 600,
            textAlign: 'center',
            lineHeight: 1.2,
            mt: 0.5,
            maxWidth: circleSize * 0.8,
            wordBreak: 'break-word',
          }}
        >
          {resolvedLabel}
        </Typography>
      </Box>
    );
  }

  // =============================================================================
  // VARIANT: Dot
  // =============================================================================
  if (variant === 'dot') {
    return (
      <Chip
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CircleIcon sx={{ fontSize: 8, color: resolvedColor }} />
            <span>{resolvedLabel}</span>
          </Box>
        }
        size={sizeMap[size].chip}
        variant="outlined"
        sx={{
          borderColor: resolvedColor,
          color: resolvedColor,
          fontWeight: 500,
          ...sx,
        }}
        className={className}
      />
    );
  }

  // =============================================================================
  // VARIANT: Pill (with icon)
  // =============================================================================
  if (variant === 'pill') {
    const isOn = isActive ?? true;

    return (
      <Chip
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isOn ? (
              <CheckCircleIcon sx={{ fontSize: 14 }} />
            ) : (
              <CancelIcon sx={{ fontSize: 14 }} />
            )}
            <span>{resolvedLabel || (isOn ? activeText : inactiveText)}</span>
          </Box>
        }
        size={sizeMap[size].chip}
        onClick={onToggle ? handleToggle : undefined}
        sx={{
          backgroundColor: isOn ? getLightColor('#22c55e') : getLightColor('#c00035'),
          color: isOn ? '#22c55e' : '#c00035',
          fontWeight: 500,
          cursor: onToggle ? 'pointer' : 'default',
          ...sx,
        }}
        className={className}
      />
    );
  }

  // =============================================================================
  // VARIANT: Chip (default)
  // =============================================================================
  return (
    <Chip
      label={resolvedLabel}
      size={sizeMap[size].chip}
      icon={icon ? <>{icon}</> : undefined}
      sx={{
        backgroundColor: resolvedColor,
        color: textColor,
        fontWeight: 600,
        fontSize: sizeMap[size].fontSize,
        ...sx,
      }}
      className={className}
    />
  );
};

export default StatusBadge;
